import { lazy } from 'react';
import { Skeleton } from '@/components/ui/skeleton';

// Lazy load heavy components for code splitting
export const LazyBrandCarousel = lazy(() => import('@/components/BrandCarousel'));
export const LazyProducts = lazy(() => import('@/components/Products'));
export const LazyContact = lazy(() => import('@/components/Contact'));

// Blog editor components - only loaded when needed (contains TipTap/ProseMirror ~100KB)
export const LazyBlogEditor = lazy(() => import('@/pages/dashboard/BlogEditor'));
export const LazyBlogList = lazy(() => import('@/pages/dashboard/BlogList'));

// Dashboard & Admin pages
export const LazyDashboardOverview = lazy(() => import('@/pages/dashboard/DashboardOverview'));
export const LazyRoleManagement = lazy(() => import('@/pages/dashboard/RoleManagement'));
export const LazySEOPerformance = lazy(() => import('@/pages/dashboard/SEOPerformance'));
export const LazyImageAudit = lazy(() => import('@/pages/dashboard/ImageAudit'));
export const LazyImageOptimizationGuide = lazy(() => import('@/components/ImageOptimizationGuide'));
export const LazyMetaPixelAnalytics = lazy(() => import('@/pages/dashboard/MetaPixelAnalytics'));
export const LazyAdminHome = lazy(() => import('@/pages/admin/AdminHome'));
export const LazyProfilo = lazy(() => import('@/pages/Profilo'));
export const LazyImpostazioni = lazy(() => import('@/pages/Impostazioni'));

// SEO Management pages
export const LazySEOManager = lazy(() => import('@/pages/dashboard/SEOManager'));
export const LazySEOPageEditor = lazy(() => import('@/pages/dashboard/SEOPageEditor'));
export const LazyRedirectManager = lazy(() => import('@/pages/dashboard/RedirectManager'));
export const LazySEOAudit = lazy(() => import('@/pages/dashboard/SEOAudit'));

// Blog Management
export const LazyCategoryManager = lazy(() => import('@/pages/dashboard/CategoryManager'));
export const LazyTagManager = lazy(() => import('@/pages/dashboard/TagManager'));
export const LazyBlogAnalytics = lazy(() => import('@/pages/dashboard/BlogAnalytics'));

// Landing Pages Management
export const LazyLandingPageList = lazy(() => import('@/pages/dashboard/LandingPageList'));
export const LazyLandingPageEditor = lazy(() => import('@/pages/dashboard/LandingPageEditor'));
export const LazyLandingPageAnalytics = lazy(() => import('@/pages/dashboard/LandingPageAnalytics'));
export const LazyLandingPageView = lazy(() => import('@/pages/LandingPageView'));

// Document Management
export const LazyDocumentManager = lazy(() => import('@/pages/dashboard/DocumentManager'));

// Hero Page Manager
export const LazyHeroPageManager = lazy(() => import('@/pages/dashboard/HeroPageManager'));

// Home Wall Manager
export const LazyHomeWallManager = lazy(() => import('@/pages/dashboard/HomeWallManager'));

// Media Library
export const LazyMediaLibrary = lazy(() => import('@/pages/dashboard/MediaLibrary'));

// Entity CMS Management
export const LazyFabbricheManager = lazy(() => import('@/pages/dashboard/entita/FabbricheManager'));
export const LazyFabbricaEditor = lazy(() => import('@/pages/dashboard/entita/FabbricaEditor'));
export const LazyMarchiManager = lazy(() => import('@/pages/dashboard/entita/MarchiManager'));
export const LazyMarchioEditor = lazy(() => import('@/pages/dashboard/entita/MarchioEditor'));
export const LazyCategorieManager2 = lazy(() => import('@/pages/dashboard/entita/CategorieManager'));
export const LazyServiziManager = lazy(() => import('@/pages/dashboard/entita/ServiziManager'));
export const LazyServizioEditor = lazy(() => import('@/pages/dashboard/entita/ServizioEditor'));
export const LazyServizioPage = lazy(() => import('@/pages/templates/ServizioPage'));

// Dynamic entity pages (replaces static brand/factory pages)
export const LazyFabbriche = lazy(() => import('@/pages/Fabbriche'));
export const LazyFabbricaPage = lazy(() => import('@/pages/templates/FabbricaPage'));
export const LazyMarchioPage = lazy(() => import('@/pages/templates/MarchioPage'));

// Other pages
export const LazyGruppo = lazy(() => import('@/pages/Gruppo'));
export const LazyMarchi = lazy(() => import('@/pages/Marchi'));
export const LazyCertificazioni = lazy(() => import('@/pages/Certificazioni'));
export const LazyProgetti = lazy(() => import('@/pages/Progetti'));
export const LazyRivista = lazy(() => import('@/pages/Rivista'));
export const LazyNotizie = lazy(() => import('@/pages/Notizie'));
export const LazyContatti = lazy(() => import('@/pages/Contatti'));
export const LazySitemap = lazy(() => import('@/pages/Sitemap'));
export const LazyArticoloDetail = lazy(() => import('@/pages/ArticoloDetail'));
export const LazyResetPassword = lazy(() => import('@/pages/ResetPassword'));

// Events & Press Kit Management
export const LazyEventManager = lazy(() => import('@/pages/dashboard/EventManager'));
export const LazyEventEditor = lazy(() => import('@/pages/dashboard/EventEditor'));
export const LazyPressKitManager = lazy(() => import('@/pages/dashboard/PressKitManager'));

// Legal pages
export const LazyPrivacy = lazy(() => import('@/pages/Privacy'));
export const LazyCookiePolicy = lazy(() => import('@/pages/CookiePolicy'));
export const LazyTerminiServizio = lazy(() => import('@/pages/TerminiServizio'));

// Shop & Emporio
export const LazyShop = lazy(() => import('@/pages/Shop'));
export const LazyShopProdotto = lazy(() => import('@/pages/ShopProdotto'));
export const LazyShopMarchio = lazy(() => import('@/pages/ShopMarchio'));
export const LazyShopFeaturedManager = lazy(() => import('@/pages/dashboard/ShopFeaturedManager'));
export const LazyEmporioMilitare = lazy(() => import('@/pages/EmporioMilitare'));

// Loading fallbacks for different component types
export const CarouselSkeleton = () => (
  <div className="py-20">
    <div className="container mx-auto px-4 md:px-6">
      <Skeleton className="h-12 w-96 mx-auto mb-12" />
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="aspect-square rounded-lg" />
        ))}
      </div>
    </div>
  </div>
);

export const ProductsSkeleton = () => (
  <div className="py-20">
    <div className="container mx-auto px-4 md:px-6">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-6">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-24 w-full" />
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-20 rounded-lg" />
            ))}
          </div>
          <div className="flex gap-4">
            <Skeleton className="h-12 w-32" />
            <Skeleton className="h-12 w-32" />
          </div>
        </div>
        <Skeleton className="aspect-[4/3] rounded-2xl" />
      </div>
    </div>
  </div>
);

export const ContactSkeleton = () => (
  <div className="py-20">
    <div className="container mx-auto px-4 md:px-6">
      <div className="grid lg:grid-cols-2 gap-12">
        <div className="space-y-6">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-6 w-full" />
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="w-5 h-5 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
          <Skeleton className="h-12 w-32" />
        </div>
      </div>
    </div>
  </div>
);

export const ServicesSkeleton = () => (
  <div className="py-20">
    <div className="container mx-auto px-4 md:px-6">
      <div className="text-center mb-12">
        <Skeleton className="h-12 w-96 mx-auto mb-4" />
        <Skeleton className="h-6 w-full max-w-2xl mx-auto" />
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-4">
            <Skeleton className="w-12 h-12 rounded-lg" />
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-20 w-full" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Page loading skeleton - minimal, fast loading indicator
export const PageSkeleton = () => (
  <div className="min-h-screen">
    <Skeleton className="h-screen w-full" />
  </div>
);

// Higher-order component for lazy loading with suspense
export const withLazyLoading = <P extends object>(
  Component: React.ComponentType<P>,
  FallbackComponent: React.ComponentType = () => <Skeleton className="w-full h-64" />
) => {
  return (props: P) => (
    <><FallbackComponent /><Component {...props} /></>
  );
};
